import { useCallback, FC, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Tooltip } from '@material-ui/core';
import { Save } from '@images/icons';
import { UpdateSubmissionRequestInterface } from '@types';
import {
  createNewSectionsAndBlocksByCurrentSubmissionOriginId,
  makeUpdateSubmission,
} from '@actions/submissionActions';
import {
  currentSubmissionDataSelector,
  currentSubmissionDescriptionSelector,
  currentSubmissionIdSelector,
  currentSubmissionLoadingSelector,
  currentSubmissionOriginSelector,
  currentSubmissionSelector,
  currentSubmissionTitleSelector,
  isCurrentSubmissionTemplateEditorModeOn,
  submissionSavableSelector,
} from '@selectors/submission';
import { UpdateSampleModal } from '@components/Modals';
import { translate } from '@utils';

export interface SaveControllerProps {
  description: string;
}

const SaveController: FC<SaveControllerProps> = ({ description }) => {
  const [isOpenModal, setIsOpenModal] = useState(false);

  const dispatch = useDispatch();

  const currentSubmission = useSelector(currentSubmissionSelector);
  const currentSubmissionData = useSelector(currentSubmissionDataSelector);
  const currentSubmissionId = useSelector(currentSubmissionIdSelector);
  const currentSubmissionTitle = useSelector(currentSubmissionTitleSelector);
  const currentSubmissionDescription = useSelector(currentSubmissionDescriptionSelector);
  const isSubmissionSavable = useSelector(submissionSavableSelector);

  const isTemplateEditorModeOn = useSelector(isCurrentSubmissionTemplateEditorModeOn);
  const isCurrentSubmissionLoading = useSelector(currentSubmissionLoadingSelector);
  const currentSubmissionOrigin = useSelector(currentSubmissionOriginSelector);

  useEffect(() => {
    if (!isTemplateEditorModeOn) {
      handleCloseModal();
    }
  }, [isTemplateEditorModeOn]);

  const handleUpdate = useCallback(() => {
    const result: UpdateSubmissionRequestInterface = currentSubmissionTitle
      ? {
          title: currentSubmissionTitle,
          description: currentSubmissionDescription,
          data: currentSubmissionData,
        }
      : { data: currentSubmissionData };
    dispatch(makeUpdateSubmission(currentSubmissionId, result));
  }, [currentSubmission, currentSubmissionId]);

  const handleOpenModal = () => {
    setIsOpenModal(true);
  };

  const handleCloseModal = () => {
    setIsOpenModal(false);
  };

  const handleSaveTemplateEditorChanges = useCallback(() => {
    dispatch(createNewSectionsAndBlocksByCurrentSubmissionOriginId());
  }, [dispatch]);

  return (
    <>
      <Tooltip title={isTemplateEditorModeOn ? translate('save_sample') : description} arrow>
        <span>
          <Button
            size="large"
            color={!currentSubmission?.isSample ? 'primary' : 'default'}
            onClick={isTemplateEditorModeOn ? handleOpenModal : handleUpdate}
            disabled={!isSubmissionSavable}
            disableRipple
          >
            <Save />
          </Button>
        </span>
      </Tooltip>
      {isTemplateEditorModeOn && (
        <UpdateSampleModal
          isOpenSaveTemplateModal={isOpenModal}
          handleCancel={handleCloseModal}
          handleConfirmation={handleSaveTemplateEditorChanges}
          isLoading={isCurrentSubmissionLoading}
          origin={currentSubmissionOrigin}
          applyChangesTestId="update-sample-modal-controller-apply"
        />
      )}
    </>
  );
};

export { SaveController };
